import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, can } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Plus, MailCheck, BellRing, CheckCircle2, Trash2, PlayCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ALERTS } from "@/constants/testIds";
import NotificationsCard from "@/components/NotificationsCard";
import { formatDMY } from "@/lib/dates";

const SEV_STYLE = {
  info: "bg-sky-500/10 text-sky-500 border-sky-500/20",
  warning: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  critical: "bg-rose-500/10 text-rose-500 border-rose-500/20",
};

const emptyForm = () => ({
  property_id: "",
  kind: "contract",
  title: "",
  description: "",
  due_date: new Date().toISOString().slice(0, 10),
  severity: "warning",
  recipient_user_ids: [],
});

export default function Alerts() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [users, setUsers] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [testing, setTesting] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testDlgOpen, setTestDlgOpen] = useState(false);
  const [runningScheduler, setRunningScheduler] = useState(false);

  const load = async () => {
    const [a, p] = await Promise.all([api.get("/alerts"), api.get("/properties")]);
    setItems(a.data);
    setProps(p.data);
    try {
      const u = await api.get("/users/directory");
      setUsers(u.data);
    } catch {
      setUsers([{ id: user.id, name: user.name, email: user.email, role: user.role }]);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/alerts", {
        ...form,
        property_id: form.property_id || null,
      });
      toast.success("Alerta creada");
      setDlgOpen(false);
      setForm(emptyForm());
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const resolve = async (id) => {
    await api.put(`/alerts/${id}`, { resolved: true });
    toast.success("Marcada como resuelta");
    load();
  };

  const del = async (id) => {
    await api.delete(`/alerts/${id}`);
    toast.success("Eliminada");
    load();
  };

  const testEmail = async () => {
    setTesting(true);
    try {
      const r = await api.post("/alerts/test-email", { to: testEmailTo || null });
      if (r.data.sent) toast.success(`Email enviado a ${r.data.recipient}`);
      else toast.warning("El servicio de email devolvió un error (revise logs).");
      setTestDlgOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setTesting(false); }
  };

  const runScheduler = async () => {
    setRunningScheduler(true);
    try {
      await api.post("/alerts/run-scheduler");
      toast.success("Scheduler ejecutado — se crearon las alertas correspondientes");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setRunningScheduler(false); }
  };

  const active = items.filter((a) => !a.resolved);
  const resolved = items.filter((a) => a.resolved);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{t("nav.alerts")}</h1>
          <div className="text-sm text-muted-foreground mt-1">{active.length} activas · {resolved.length} resueltas</div>
        </div>
        <div className="flex gap-2">
          {can(user, "sendTestEmail") && (
            <>
              <Button variant="outline" onClick={runScheduler} disabled={runningScheduler} data-testid="alerts-run-scheduler">
                <PlayCircle className="w-4 h-4 mr-1" /> {runningScheduler ? "Ejecutando…" : "Revisar vencimientos ahora"}
              </Button>
              <Button variant="outline" onClick={() => { setTestEmailTo(user.email); setTestDlgOpen(true); }} data-testid={ALERTS.testEmailBtn}>
                <MailCheck className="w-4 h-4 mr-1" /> {t("alerts.testEmail")}
              </Button>
            </>
          )}
          {can(user, "manageAlerts") && (
            <Button className="rounded-full" onClick={() => setDlgOpen(true)} data-testid={ALERTS.addBtn}>
              <Plus className="w-4 h-4 mr-1" /> {t("alerts.add")}
            </Button>
          )}
        </div>
      </div>

      <NotificationsCard />

      <div className="space-y-3" data-testid={ALERTS.list}>
        {active.map((a) => (
          <div key={a.id} className={`flex items-start gap-3 rounded-xl border p-4 sm:p-5 bg-card ${SEV_STYLE[a.severity] || ""}`}>
            <BellRing className="w-4 h-4 mt-1 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium text-foreground">{a.title}</div>
                <span className="text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 border-current">{a.severity}</span>
                {a.email_sent && <span className="text-[10px] text-muted-foreground">✉ email enviado</span>}
              </div>
              {a.description && <div className="text-sm text-muted-foreground mt-1">{a.description}</div>}
              <div className="text-xs mt-1 num">Vence: {formatDMY(a.due_date)}</div>
            </div>
            {can(user, "manageAlerts") && (
              <div className="flex gap-1 shrink-0">
                <Button size="icon" variant="ghost" onClick={() => resolve(a.id)} data-testid={`${ALERTS.resolveBtn}-${a.id}`}>
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => del(a.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
        {!active.length && (
          <div className="rounded-xl border border-border/60 bg-card p-8 text-center text-muted-foreground text-sm">
            Sin alertas activas. Todo bajo control.
          </div>
        )}
      </div>

      {!!resolved.length && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-3">Resueltas</div>
          <div className="space-y-2 opacity-70">
            {resolved.map((a) => (
              <div key={a.id} className="rounded-lg border border-border/60 bg-card p-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <div className="flex-1 text-sm">{a.title}</div>
                <div className="text-xs text-muted-foreground num">{formatDMY(a.due_date)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid={ALERTS.dialog} className="max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Nueva alerta</DialogTitle>
            <DialogDescription>Las alertas de severidad Advertencia o Crítica intentan enviar un email al creador.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Título</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="alert-form-title" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contract">Contrato</SelectItem>
                  <SelectItem value="insurance">Seguro</SelectItem>
                  <SelectItem value="tax">Impuesto</SelectItem>
                  <SelectItem value="payment">Pago</SelectItem>
                  <SelectItem value="custom">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Severidad</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Advertencia</SelectItem>
                  <SelectItem value="critical">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Propiedad (opcional)</Label>
              <Select value={form.property_id || "none"} onValueChange={(v) => setForm({ ...form, property_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Ninguna" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguna</SelectItem>
                  {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vence</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Descripción</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>

            <div className="sm:col-span-2">
              <Label>Destinatarios del email (opcional)</Label>
              <div className="text-[11px] text-muted-foreground mb-2">
                Si no seleccionás a nadie, se usa la configuración por defecto de cada usuario. Si tildás uno o más, el email se envía SOLO a esos.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                {users.map((u) => {
                  const active = form.recipient_user_ids.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                        active ? "border-foreground/40 bg-secondary" : "border-border/60 hover:border-foreground/20"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="accent-foreground"
                        checked={active}
                        onChange={() => {
                          const cur = new Set(form.recipient_user_ids);
                          if (cur.has(u.id)) cur.delete(u.id); else cur.add(u.id);
                          setForm({ ...form, recipient_user_ids: Array.from(cur) });
                        }}
                        data-testid={`alert-recipient-${u.id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate">{u.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{u.role}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => setDlgOpen(false)}>Cancelar</Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={testDlgOpen} onOpenChange={setTestDlgOpen}>
        <DialogContent data-testid="alerts-test-email-dialog" className="max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Enviar email de prueba</DialogTitle>
            <DialogDescription>
              Ingresá el email al que querés enviar la prueba. Verificá que sea un email real que puedas revisar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Enviar a</Label>
              <Input
                type="email"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                placeholder="tu-email-real@gmail.com"
                data-testid="alerts-test-email-input"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                ⚠️ Las cuentas demo (admin@family.com, editor@family.com, etc.) no reciben emails reales porque no existen. Usá tu email personal.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDlgOpen(false)}>Cancelar</Button>
            <Button onClick={testEmail} disabled={testing || !testEmailTo}>
              {testing ? "Enviando…" : "Enviar prueba"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
